import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Enterprise color palette with gradient theme
const colors = {
  light: {
    // Primary: Gradient colors
    primary: '#9358f7',
    primaryLight: '#a066f9',
    primaryDark: '#7c4ae3',

    // Secondary: Gradient colors
    secondary: '#6197ee',
    secondaryLight: '#7aa6f1',
    secondaryDark: '#4d7bdc',

    // Accent: Gradient colors
    accent: '#10d7e2',
    accentLight: '#2fdfeb',
    accentDark: '#0cb5c9',

    // Backgrounds and text
    background: '#ffffff',
    surface: '#f8fafc',
    surfaceAlt: '#f1f5f9',
    text: '#1e293b',
    textSecondary: '#64748b',
    textTertiary: '#94a3b8',
    border: '#e2e8f0',
    borderLight: '#f1f5f9',
    divider: '#cbd5e1'
  },
  dark: {
    // Primary: Gradient colors (adjusted for dark mode)
    primary: '#9358f7',
    primaryLight: '#a066f9',
    primaryDark: '#7c4ae3',

    // Secondary: Gradient colors
    secondary: '#6197ee',
    secondaryLight: '#7aa6f1',
    secondaryDark: '#4d7bdc',

    // Accent: Gradient colors
    accent: '#10d7e2',
    accentLight: '#2fdfeb',
    accentDark: '#0cb5c9',

    // Backgrounds and text (deep charcoal, not pure black)
    background: '#000000',
    surface: '#1e293b',
    surfaceAlt: '#334155',
    text: '#ffffff',
    textSecondary: '#cbd5e1',
    textTertiary: '#94a3b8',
    border: '#334155',
    borderLight: '#475569',
    divider: '#475569'
  }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev: boolean) => !prev);
  };

  const palette = isDarkMode ? colors.dark : colors.light;

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: { main: palette.primary },
      secondary: { main: palette.accent },
      background: {
        default: palette.background,
        paper: palette.surface
      },
      text: {
        primary: palette.text,
        secondary: palette.textSecondary
      },
      divider: palette.border,
      success: { main: '#22c55e' },
      warning: { main: '#eab308' },
      error: { main: '#ef4444' },
      info: { main: '#3b82f6' }
    },
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif',
      h1: { fontSize: '32px', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.5px' },
      h2: { fontSize: '28px', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.3px' },
      h3: { fontSize: '24px', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.2px' },
      h4: { fontSize: '20px', fontWeight: 600, lineHeight: 1.4 },
      h5: { fontSize: '18px', fontWeight: 500, lineHeight: 1.4 },
      h6: { fontSize: '16px', fontWeight: 500, lineHeight: 1.5 },
      subtitle1: { fontSize: '16px', fontWeight: 500, lineHeight: 1.5 },
      subtitle2: { fontSize: '14px', fontWeight: 500, lineHeight: 1.5 },
      body1: { fontSize: '14px', fontWeight: 400, lineHeight: 1.6 },
      body2: { fontSize: '13px', fontWeight: 400, lineHeight: 1.6 },
      caption: { fontSize: '12px', fontWeight: 400, lineHeight: 1.5, color: palette.textSecondary },
      button: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '14px',
        letterSpacing: '0px'
      }
    },
    spacing: 8,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: palette.background,
            color: palette.text,
            transition: 'background-color 200ms ease, color 200ms ease'
          },
          // Invert the native browser date/time picker icon in dark mode
          ...(isDarkMode && {
            'input[type="date"]::-webkit-calendar-picker-indicator, input[type="time"]::-webkit-calendar-picker-indicator, input[type="month"]::-webkit-calendar-picker-indicator': {
              filter: 'invert(1)',
              opacity: 0.7,
              cursor: 'pointer',
            },
          }),
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            padding: '10px 16px',
            transition: 'all 150ms ease-in-out',
            fontSize: '14px',
            fontWeight: 500,
            textTransform: 'none',
            '&:active': {
              transform: 'scale(0.98)'
            },
            '&:disabled': {
              opacity: 0.5
            }
          },
          contained: {
            background: 'linear-gradient(45deg, #9358f7, #6197ee, #10d7e2)',
            color: '#ffffff',
            boxShadow: `0 1px 3px ${isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
            '&:hover': {
              background: 'linear-gradient(45deg, #a066f9, #7aa6f1, #2fdfeb)',
              boxShadow: `0 4px 12px ${isDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.15)'}`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              backgroundColor: palette.border,
              background: palette.border,
              color: palette.textTertiary,
              boxShadow: 'none'
            }
          },
          outlined: {
            borderColor: '#9358f7',
            color: '#9358f7',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(147, 88, 247, 0.08)' : 'rgba(147, 88, 247, 0.04)',
              borderColor: '#a066f9'
            }
          },
          text: {
            color: '#9358f7',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(147, 88, 247, 0.08)' : 'rgba(147, 88, 247, 0.04)'
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: palette.textSecondary,
            transition: 'all 150ms ease-in-out',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              color: palette.primary
            }
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.surface,
            borderRight: `1px solid ${palette.border}`,
            boxShadow: 'none'
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: palette.surface,
            color: palette.text,
            borderBottom: `1px solid ${palette.border}`,
            boxShadow: `0 1px 3px ${isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)'}`
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: palette.surface,
            boxShadow: `0 1px 3px ${isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.08)'}`,
            border: `1px solid ${palette.border}`,
            transition: 'all 150ms ease-in-out',
            '&:hover': {
              boxShadow: `0 4px 12px ${isDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.12)'}`,
              transform: 'translateY(-2px)'
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: palette.surface,
            backgroundImage: 'none',
            transition: 'all 150ms ease-in-out'
          },
          outlined: {
            borderColor: palette.border
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              transition: 'all 150ms ease-in-out',
              backgroundColor: palette.surface,
              '& fieldset': {
                borderColor: palette.border
              },
              '&:hover fieldset': {
                borderColor: palette.secondary
              },
              '&.Mui-focused fieldset': {
                borderColor: palette.primary,
                borderWidth: '2px'
              }
            },
            '& .MuiInputBase-input': {
              fontSize: '14px',
              color: palette.text,
              '&::placeholder': {
                color: palette.textTertiary,
                opacity: 1
              }
            },
            '& .MuiInputAdornment-root': {
              color: palette.textSecondary
            }
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              backgroundColor: palette.borderLight,
              color: palette.textTertiary
            }
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: palette.textSecondary,
            fontWeight: 500,
            fontSize: '14px',
            marginBottom: '6px',
            '&.Mui-focused': {
              color: palette.primary
            }
          }
        }
      },
      MuiTable: {
        styleOverrides: {
          root: {
            backgroundColor: palette.surface,
            '& thead': {
              backgroundColor: palette.surfaceAlt
            }
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 150ms ease-in-out',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)'
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: palette.border,
            padding: '12px 16px'
          },
          head: {
            fontWeight: 600,
            color: palette.text,
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            backgroundColor: palette.surfaceAlt
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: palette.surfaceAlt,
            color: palette.text,
            borderRadius: '6px',
            fontWeight: 500,
            border: `1px solid ${palette.border}`,
            fontSize: '13px',
            height: 'auto',
            padding: '4px 8px'
          },
          colorPrimary: {
            backgroundColor: isDarkMode ? 'rgba(147, 88, 247, 0.12)' : 'rgba(147, 88, 247, 0.1)',
            color: '#9358f7',
            border: `1px solid ${isDarkMode ? 'rgba(147, 88, 247, 0.3)' : 'rgba(147, 88, 247, 0.2)'}`
          },
          colorSuccess: {
            backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.1)',
            color: '#22c55e',
            border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.2)'}`
          },
          colorError: {
            backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            border: `1px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)'}`
          },
          colorWarning: {
            backgroundColor: isDarkMode ? 'rgba(234, 179, 8, 0.12)' : 'rgba(234, 179, 8, 0.1)',
            color: '#eab308',
            border: `1px solid ${isDarkMode ? 'rgba(234, 179, 8, 0.3)' : 'rgba(234, 179, 8, 0.2)'}`
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.surface,
            boxShadow: `0 20px 25px -5px ${isDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.1)'}`,
            borderRadius: '8px',
            border: `1px solid ${palette.border}`
          }
        }
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: palette.borderLight,
            borderRadius: '3px',
            height: '6px'
          },
          bar: {
            borderRadius: '3px',
            backgroundColor: palette.accent
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-input': {
              color: palette.text
            }
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
            },
            '&.Mui-selected': {
              backgroundColor: isDarkMode ? 'rgba(15, 118, 110, 0.12)' : 'rgba(15, 118, 110, 0.1)',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(15, 118, 110, 0.18)' : 'rgba(15, 118, 110, 0.15)'
              }
            }
          }
        }
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: palette.accent,
            textDecoration: 'none',
            transition: 'color 150ms ease-in-out',
            '&:hover': {
              color: palette.accentLight
            }
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            border: `1px solid`,
            fontSize: '14px'
          },
          standardSuccess: {
            backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.08)',
            borderColor: isDarkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.2)',
            color: palette.text
          },
          standardError: {
            backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)',
            borderColor: isDarkMode ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)',
            color: palette.text
          },
          standardWarning: {
            backgroundColor: isDarkMode ? 'rgba(234, 179, 8, 0.1)' : 'rgba(234, 179, 8, 0.08)',
            borderColor: isDarkMode ? 'rgba(234, 179, 8, 0.3)' : 'rgba(234, 179, 8, 0.2)',
            color: palette.text
          },
          standardInfo: {
            backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.08)',
            borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)',
            color: palette.text
          }
        }
      }
    }
  });

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
